import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Sparkle, Copy, CheckCircle, ChatsCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PlatformChip } from "../lib/meta";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Explainer, WarmEmptyState } from "../components/friendly";

const SENTIMENT = { positive: "bg-emerald-50 text-emerald-700", neutral: "bg-slate-100 text-slate-600", negative: "bg-red-50 text-red-600" };

export default function Engage() {
  const { activeClientId, refreshOrg } = useApp();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (activeClientId) api.get(`/engage?client_id=${activeClientId}`).then((r) => setItems(r.data));
  }, [activeClientId]);
  useEffect(load, [load]);

  const draftReply = async (id) => {
    setBusy(id);
    try {
      await api.post("/ai/reply", { item_id: id });
      refreshOrg(); load();
      toast.success("Reply drafted (1 credit)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to draft reply");
    } finally { setBusy(""); }
  };

  const copyReply = async (text) => { await navigator.clipboard.writeText(text); toast.success("Reply copied — send it natively"); };
  const markReplied = async (id) => { await api.post(`/engage/${id}/mark-replied`); load(); toast.success("Marked replied"); };
  const updateReply = (id, v) => setItems(items.map((it) => (it.item_id === id ? { ...it, ai_reply: v } : it)));

  return (
    <div className="space-y-6 fade-up max-w-3xl" data-testid="engage-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Engage</h1>
        <p className="text-sm text-slate-500 mt-1">Mentions, comments & DMs (mock feed in v1). AI drafts the reply — you send it natively.</p>
      </div>
      <Explainer id="engage">All comments, mentions and DMs in one stream (sample data in v1). Ask for a draft reply and the co-pilot suggests an answer in your brand's voice — you edit it and send it on the platform.</Explainer>

      <div className="space-y-4">
        {items.map((it) => (
          <div key={it.item_id} data-testid={`engage-item-${it.item_id}`} className={`bg-white border border-slate-200/80 rounded-xl p-5 card-float ${it.status === "replied" ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-semibold">@{it.author}</span>
              <PlatformChip platform={it.platform} />
              <span className="text-[10px] uppercase tracking-widest text-slate-400">{it.type}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${SENTIMENT[it.sentiment] || SENTIMENT.neutral}`}>{it.sentiment}</span>
              {it.status === "replied" && <span className="ml-auto text-[11px] text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle size={13} weight="fill" /> Replied</span>}
            </div>
            <p className="mt-2 text-[13px] text-slate-700 leading-relaxed">{it.text}</p>
            {it.ai_reply ? (
              <div className="mt-3 space-y-2">
                <Textarea data-testid={`reply-text-${it.item_id}`} rows={2} value={it.ai_reply} onChange={(e) => updateReply(it.item_id, e.target.value)} className="text-[13px]" />
                <div className="flex gap-2">
                  <Button data-testid={`copy-reply-${it.item_id}`} size="sm" variant="outline" className="rounded-lg gap-1.5" onClick={() => copyReply(it.ai_reply)}><Copy size={13} /> Copy reply</Button>
                  <Button data-testid={`mark-replied-${it.item_id}`} size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-500" onClick={() => markReplied(it.item_id)}>Mark replied</Button>
                </div>
              </div>
            ) : it.status !== "replied" && (
              <Button data-testid={`draft-reply-${it.item_id}`} size="sm" variant="outline" className="mt-3 rounded-lg gap-1.5" disabled={busy === it.item_id} onClick={() => draftReply(it.item_id)}>
                <Sparkle size={13} weight="fill" className="text-[hsl(9,69%,62%)]" /> {busy === it.item_id ? "Drafting…" : "AI draft reply (1 credit)"}
              </Button>
            )}
          </div>
        ))}
        {!items.length && <WarmEmptyState icon={ChatsCircle} title="No conversations here yet" what="Comments, mentions and DMs for this brand will gather in this stream, with AI-suggested replies in your voice ready to edit and send." />}
      </div>
    </div>
  );
}
